import React, { useEffect, useState, useRef } from 'react';
import { RefreshCcw } from 'lucide-react';
import api, {
  getWahaSessionStatus,
  startWahaSession,
  stopWahaSession,
  connectWaha,
  requestWahaPairingCode,
  getOfficialWhatsAppConfig,
  refreshOfficialWhatsAppConnection,
  disconnectOfficialWhatsAppConnection,
  type OfficialWhatsAppConfig
} from '../services/api.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { useWhatsAppEmbeddedSignup } from '../hooks/useWhatsAppEmbeddedSignup.ts';
import { useInstagramConnection } from '../hooks/useInstagramConnection.ts';
import { AddChannelButton, ChannelPicker, ChannelsList, channelsTheme, instagramChannelRow, whatsappChannelRows, type ChannelRow } from '../components/channels/ChannelsUI.tsx';
import { InstagramConnectionPanel, InstagramDisconnectModal } from '../components/channels/InstagramConnectionPanel.tsx';
import {
  WhatsAppAlert,
  WhatsAppConfirmModal,
  WhatsAppOfficialAccountCard,
  WhatsAppPairingPanel,
  type WhatsAppDeviceData as DeviceData,
  type WhatsAppProvider as Provider,
  type WhatsAppStatusData as StatusData,
} from '../components/whatsapp-connect/WhatsAppConnectUI.tsx';

import { ConnectionBack, ConnectionButton, ConnectionDeleteButton, ConnectionIdentity, ConnectionLink, connectionPanelClass, ConnectionPicker, DeviceConnectionProfile, connectionTheme, getConnectionRows, hasOfficialConnection, type ConnectionLoadState, type ConnectionView } from '../components/whatsapp-connect/WhatsAppConnections.tsx';

const WhatsAppConnectPage: React.FC = () => {
  const { isDark } = useTheme();
  const [view, setView] = useState<ConnectionView | 'channel-picker' | 'instagram'>(() => {
    const query = new URLSearchParams(window.location.search);
    return query.has('instagram') || query.has('instagram_status') || query.get('canal') === 'instagram' ? 'instagram' : 'list';
  });
  const instagram = useInstagramConnection();
  const [showInstagramDisconnect, setShowInstagramDisconnect] = useState(false);
  const [deviceLoad, setDeviceLoad] = useState<ConnectionLoadState>('loading');
  const pairingActiveRef = useRef(false);
  const pairingModeRef = useRef<'qr' | 'pairing'>('qr');
  const qrRequestRef = useRef(false);
  const qrFailedRef = useRef(false);
  const qrErrorCountRef = useRef(0);
  const pairingGenerationRef = useRef(0);
  const operationRef = useRef(false);
  const statusRequestRef = useRef(false);


  // Z-API states

  // WAHA states
  const [wahaSessionName, setWahaSessionName] = useState('');

  // Provider management
  const [currentProvider, setCurrentProvider] = useState<Provider>(null);

  // Common states
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [deviceData, setDeviceData] = useState<DeviceData | null>(null);
  const [statusData, setStatusData] = useState<StatusData | null>(null);
  const [qrcodeBase64, setQrcodeBase64] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [qrAttempts, setQrAttempts] = useState(0);
  const [wahaConnectionMode, setWahaConnectionMode] = useState<'qr' | 'pairing'>('qr');
  const [wahaPairingPhone, setWahaPairingPhone] = useState('');
  const [wahaPairingCode, setWahaPairingCode] = useState('');
  const [isPairingLoading, setIsPairingLoading] = useState(false);
  const qrIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Refs para manter valores atualizados nos intervals (evitar stale closure)
  const currentProviderRef = useRef<Provider>(currentProvider);
  const statusDataRef = useRef<StatusData | null>(statusData);

  // Modals
  const [showResetModal, setShowResetModal] = useState(false);

  // Official WhatsApp API
  const [officialConfig, setOfficialConfig] = useState<OfficialWhatsAppConfig | null>(null);
  const [officialConnectionLoadState, setOfficialConnectionLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [officialNotice, setOfficialNotice] = useState('');
  const [isOfficialLoading, setIsOfficialLoading] = useState(false);
  const [showOfficialDisconnectModal, setShowOfficialDisconnectModal] = useState(false);

  useEffect(() => {
    // Onboarding/refresh snapshots omit the optional profile; GET resolves it separately.
    if (officialConfig?.status !== 'connected' || !officialConfig.phone_id
      || officialConfig.profile_picture_url !== undefined) return;
    let active = true;
    void getOfficialWhatsAppConfig().then(snapshot => {
      if (!active || snapshot.status !== 'connected' || snapshot.phone_id !== officialConfig.phone_id) return;
      setOfficialConfig(current => current === officialConfig
        ? { ...current, profile_picture_url: snapshot.profile_picture_url || null }
        : current);
    }).catch(() => {
      // A missing photo must not replace a successful connection snapshot with an error.
    });
    return () => { active = false; };
  }, [officialConfig]);

  const fetchOfficialConnection = async () => {
    const data = await getOfficialWhatsAppConfig();
    setOfficialConfig(data);
    setOfficialConnectionLoadState('ready');
    return data;
  };

  const embeddedSignup = useWhatsAppEmbeddedSignup({
    onConnected: (connection) => {
      setOfficialConfig(connection);
      setOfficialConnectionLoadState('ready');
      setError('');
      if (connection.has_official || connection.status === 'connected') {
        setOfficialNotice('');
        setMessage('WhatsApp Oficial conectado com sucesso.');
      } else if (connection.status === 'provisioning') {
        setMessage('');
        setOfficialNotice('Conta autorizada. A ativação do número está em andamento na Meta.');
      } else {
        setMessage('');
        setOfficialNotice('A autorização foi recebida. Verifique o andamento da ativação.');
      }
    },
    onConnectionSnapshot: (connection) => {
      setOfficialConfig(connection);
      setOfficialConnectionLoadState('ready');
      if (connection.status === 'provisioning') {
        setMessage('');
        setOfficialNotice('Conta autorizada. A ativação do número está em andamento na Meta.');
      }
    },
    onError: (signupError) => {
      setOfficialNotice('');
      setMessage('');
      setError(signupError);
    },
    onCancelled: (signupMessage) => {
      setError('');
      setMessage('');
      setOfficialNotice(signupMessage);
    },
  });

  const handleConnectOfficial = () => {
    setOfficialNotice('');
    setError('');
    setMessage('');
    embeddedSignup.connect();
  };

  const handleRetryOfficialPreparation = () => {
    setOfficialNotice('');
    setError('');
    setMessage('');
    setOfficialConnectionLoadState('loading');
    void fetchOfficialConnection().catch(() => {
      setOfficialConnectionLoadState('error');
    });
    void embeddedSignup.prepare();
  };

  const handleRefreshOfficial = async () => {
    setIsOfficialLoading(true);
    setOfficialNotice('');
    setError('');
    setMessage('');
    try {
      const refreshed = await refreshOfficialWhatsAppConnection();
      setOfficialConfig(refreshed);
      setOfficialConnectionLoadState('ready');
      if (refreshed.has_official || refreshed.status === 'connected') {
        setMessage('WhatsApp Oficial conectado e verificado.');
      } else if (refreshed.status === 'provisioning') {
        setOfficialNotice('Conta autorizada. A ativação do número ainda está em andamento na Meta.');
      } else {
        setError(refreshed.last_error || 'A conexão oficial precisa de atenção.');
      }
    } catch (refreshError: any) {
      const refreshMessage = refreshError.response?.data?.detail
        || 'A conexão oficial precisa de atenção.';
      try {
        const snapshot = await fetchOfficialConnection();
        if (snapshot.has_official || snapshot.status === 'connected') {
          setMessage('WhatsApp Oficial conectado e verificado.');
        } else if (snapshot.status === 'provisioning') {
          setOfficialNotice('Conta autorizada. A ativação do número ainda está em andamento na Meta.');
        } else {
          setError(refreshMessage);
        }
      } catch {
        setOfficialConnectionLoadState('error');
        setError(refreshMessage);
      }
    } finally {
      setIsOfficialLoading(false);
    }
  };

  const handleDisconnectOfficial = async () => {
    setShowOfficialDisconnectModal(false);
    setIsOfficialLoading(true);
    setOfficialNotice('');
    setError('');
    try {
      await disconnectOfficialWhatsAppConnection();
      setOfficialConfig({ has_official: false, status: 'disconnected', mode: 'embedded_signup' });
      setMessage('WhatsApp Oficial desconectado. O WhatsApp por dispositivo não foi alterado.');
    } catch (disconnectError: any) {
      const disconnectMessage = disconnectError.response?.data?.detail
        || 'Não foi possível desconectar o WhatsApp Oficial.';
      try {
        await fetchOfficialConnection();
      } catch {
        // A mensagem da tentativa de desconexão continua sendo o erro principal.
      }
      setError(disconnectMessage);
    } finally {
      setIsOfficialLoading(false);
    }
  };

  const sanitizeWhatsAppUiText = (text: string) => (
    text
      .replace(/\bWAHA\b/gi, 'WhatsApp')
      .replace(/\bZ-API\b/gi, 'WhatsApp')
      .replace(/\bWPPConnect\b/gi, 'WhatsApp')
      .replace(/session_name/gi, 'identificador interno')
      .replace(/\bsess[aã]o\b/gi, 'conexao')
      .replace(/\bsessões\b/gi, 'conexoes')
      .replace(/\bprovider\b/gi, 'canal')
  );

  const clearStates = () => {
    setError('');
    setMessage('');
    setDeviceData(null);
    setStatusData(null);
    setQrcodeBase64('');
    setQrAttempts(0);
    setWahaPairingCode('');
  };

  // Sincronizar refs com states (effect)
  useEffect(() => {
    currentProviderRef.current = currentProvider;
  }, [currentProvider]);

  useEffect(() => {
    statusDataRef.current = statusData;
  }, [statusData]);

  // ==========================================
  // UNIFIED CONFIG DETECTION
  // ==========================================

  async function fetchWhatsAppConfig(): Promise<{ provider: Provider; hasConfig: boolean }> {
    try {
      const resp = await api.get('/webhook/whatsapp/config');
      const { provider, config } = resp.data;


      if (!provider || !config) {
        setCurrentProvider(null);
        currentProviderRef.current = null;
        setDeviceData(null);
        setStatusData(null);
        setDeviceLoad('ready');
        return { provider: null, hasConfig: false };
      }

      // Detectar WAHA
      if (provider === 'waha') {
        setWahaSessionName(config.session_name);
        setCurrentProvider('waha');
        currentProviderRef.current = 'waha';
        return { provider: 'waha', hasConfig: true };
      }

      // Detectar Z-API
      if (provider === 'zapi') {
        setCurrentProvider('zapi');
        currentProviderRef.current = 'zapi';
        return { provider: 'zapi', hasConfig: true };
      }

      setDeviceLoad('ready');
      return { provider: null, hasConfig: false };
    } catch (err) {
      setDeviceLoad('error');
      console.error('Erro ao consultar configuração de dispositivo');
      return { provider: null, hasConfig: false };
    }
  }

  // ==========================================
  // Z-API FUNCTIONS (existentes)
  // ==========================================

  async function fetchZapiStatus() {
    try {
      const resp = await api.get('/webhook/whatsapp/status');
      if (typeof resp.data.connected !== 'boolean') throw new Error('Status indisponível');
      setDeviceLoad('ready');
      statusDataRef.current = resp.data;
      setStatusData(resp.data);
      return resp.data as StatusData;
    } catch (err) {
      setDeviceLoad('error');
      console.error('Erro ao verificar status Z-API:', err);
      setError('Não foi possível verificar o status da conexão do WhatsApp.');
      return null;
    }
  }

  async function fetchZapiDevice() {
    try {
      const resp = await api.get('/webhook/whatsapp/device');
      setDeviceData(resp.data);
    } catch (err) {
      console.error('Erro ao obter dados do device Z-API:', err);
    }
  }

  async function fetchZapiQRCode() {
    const generation = pairingGenerationRef.current;
    if (!pairingActiveRef.current || pairingModeRef.current !== 'qr' || statusDataRef.current?.connected) return;
    try {
      const response = await api.get('/webhook/whatsapp/qrcode');
      if (!pairingActiveRef.current || generation !== pairingGenerationRef.current) return;
      const code = response.data.qrcode;
      if (typeof code === 'string' && code) setQrcodeBase64(code.startsWith('data:') ? code : `data:image/png;base64,${code}`);
      setQrAttempts(previous => previous + 1);
    } catch {
      if (!pairingActiveRef.current || generation !== pairingGenerationRef.current) return;
      qrFailedRef.current = true;
      setError('Não foi possível gerar o QR Code. Tente novamente.');
      stopQrInterval();
    }
  }

  // ==========================================
  // WAHA FUNCTIONS
  // ==========================================

  async function fetchWahaStatus() {
    try {
      const resp = await api.get('/webhook/whatsapp/status');
      if (typeof resp.data.connected !== 'boolean') throw new Error('Status indisponível');
      setDeviceLoad('ready');
      const statusData: StatusData = {
        connected: resp.data.connected,
        state: resp.data.state || resp.data.status
      };
      statusDataRef.current = statusData;
      setStatusData(statusData);
      return statusData;
    } catch (err) {
      setDeviceLoad('error');
      console.error('Erro ao verificar status WAHA:', err);
      setError('Não foi possível verificar o status da conexão do whatsapp.');
      return null;
    }
  }

  async function fetchWahaDevice() {
    try {
      const resp = await api.get('/webhook/whatsapp/device');
      const data = resp.data;
      const deviceData: DeviceData = {
        id: data.id || '',
        name: data.name || data.pushname || '',
        phone: data.phone || data.me?.id?.user || '',
        imgUrl: data.imgUrl || '',
        isBusiness: data.isBusiness || false,
        device: {
          sessionName: wahaSessionName,
          device_model: data.platform || 'WhatsApp'
        }
      };
      setDeviceData(deviceData);
    } catch (err) {
      console.error('Erro ao obter dados do device WAHA:', err);
    }
  }

  async function fetchWahaQRCode() {
    const generation = pairingGenerationRef.current;
    const active = () => pairingActiveRef.current && pairingModeRef.current === 'qr'
      && generation === pairingGenerationRef.current;
    if (!active() || statusDataRef.current?.connected) return;
    try {
      const session = await getWahaSessionStatus();
      if (!active()) return;
      if (session.connected) {
        await handleFetchAllData(currentProviderRef.current);
      } else if (session.needsStart || session.status === 'NOT_FOUND') {
        await startWahaSession();
      } else if (session.status === 'FAILED') {
        await stopWahaSession();
        if (!active()) return;
        await startWahaSession();
      } else if (session.needsQR) {
        const response = await api.get('/webhook/whatsapp/qrcode');
        if (!active()) return;
        const code = response.data.qrcode;
        if (typeof code === 'string' && code) {
          setQrcodeBase64(code.startsWith('data:') ? code : `data:image/png;base64,${code}`);
        }
      }
      if (active()) setQrAttempts(previous => previous + 1);
    } catch (err: any) {
      if (!active()) return;
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && /já está conectado|already connected/i.test(detail)) {
        await handleFetchAllData(currentProviderRef.current);
      } else if (err.response?.status === 422) {
        try { await startWahaSession(); }
        catch {
          qrFailedRef.current = true;
          setError('Não foi possível preparar o QR Code. Tente novamente.');
          stopQrInterval();
        }
      } else if (err.response?.status === 400) {
        qrErrorCountRef.current += 1;
        setQrAttempts(previous => previous + 1);
        if (qrErrorCountRef.current >= 10) {
          qrFailedRef.current = true;
          setError('O QR Code não ficou disponível. Tente novamente.');
          stopQrInterval();
        }
      } else {
        qrFailedRef.current = true;
        setError('Não foi possível gerar o QR Code. Tente novamente.');
        stopQrInterval();
      }
    }
  }

  // ==========================================
  // UNIFIED FUNCTIONS (multi-provider)
  // ==========================================

  async function fetchStatus() {
    // Usar ref para evitar stale closure em setInterval
    const provider = currentProviderRef.current;

    if (provider === 'zapi') {
      return await fetchZapiStatus();
    } else if (provider === 'waha') {
      return await fetchWahaStatus();
    }
    console.warn('⚠️ [fetchStatus] Provider inválido ou null:', provider);
    return null;
  }

  async function fetchDeviceData() {
    // Usar ref para evitar stale closure em setInterval
    const provider = currentProviderRef.current;

    if (provider === 'zapi') {
      await fetchZapiDevice();
    } else if (provider === 'waha') {
      await fetchWahaDevice();
    }
  }

  async function fetchQrCode() {
    if (!pairingActiveRef.current || pairingModeRef.current !== 'qr' || qrRequestRef.current) return;
    qrRequestRef.current = true;
    try {
      if (currentProviderRef.current === 'zapi') await fetchZapiQRCode();
      else if (currentProviderRef.current === 'waha') await fetchWahaQRCode();
    } finally { qrRequestRef.current = false; }
  }

  function startQrInterval() {
    if (!pairingActiveRef.current || pairingModeRef.current !== 'qr' || qrFailedRef.current) return;
    if (qrIntervalRef.current) {
      clearInterval(qrIntervalRef.current);
    }
    qrIntervalRef.current = setInterval(() => {
      fetchQrCode();
    }, 3000); // Reduzido para 3s para garantir QR sempre fresco
  }

  function stopQrInterval() {
    if (qrIntervalRef.current) {
      clearInterval(qrIntervalRef.current);
      qrIntervalRef.current = null;
    }
  }

  function startStatusCheckInterval() {
    if (!pairingActiveRef.current) return;
    // Parar intervalo existente se houver
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
    }


    // Verificar status a cada 3 segundos para detectar quando conectar (mais rápido)
    statusCheckIntervalRef.current = setInterval(async () => {
      if (!pairingActiveRef.current || statusRequestRef.current) return;
      statusRequestRef.current = true;
      const generation = pairingGenerationRef.current;
      try {

        const status = await fetchStatus();
        if (!pairingActiveRef.current || generation !== pairingGenerationRef.current) return;

        if (status?.connected) {

          // Parar intervalos primeiro
          stopQrInterval();
          stopStatusCheckInterval();

          // Atualizar todos os states
          setStatusData({ ...status, connected: true }); // Force new object reference
          setQrcodeBase64(''); // Limpar QR code
          setQrAttempts(0); // Resetar tentativas
          setWahaPairingCode('');
          pairingActiveRef.current = false;
          setView('device');
          setMessage('WhatsApp conectado com sucesso!');

          // Buscar dados do device
          await fetchDeviceData();

        }
      } catch (err) {
        console.error('Erro ao verificar status no polling');
      } finally { statusRequestRef.current = false; }
    }, 3000); // Reduzido de 5s para 3s
  }

  function stopStatusCheckInterval() {
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
  }

  async function handleFetchAllData(provider?: Provider, _forceRefresh = false) {
    const selected = provider || currentProviderRef.current;
    setIsLoading(true);
    setDeviceLoad('loading');
    try {
      const status = selected === 'waha' ? await fetchWahaStatus() : selected === 'zapi' ? await fetchZapiStatus() : null;
      if (!status) return;
      if (status.connected) {
        stopQrInterval();
        stopStatusCheckInterval();
        setQrcodeBase64('');
        setWahaPairingCode('');
        if (selected === 'waha') await fetchWahaDevice();
        else await fetchZapiDevice();
        if (pairingActiveRef.current) {
          pairingActiveRef.current = false;
          setView('device');
        }
      } else if (pairingActiveRef.current) {
        await fetchQrCode();
        startQrInterval();
        startStatusCheckInterval();
      }
    } finally { setIsLoading(false); }
  }

  function leaveFlow() {
    pairingActiveRef.current = false;
    pairingGenerationRef.current += 1;
    stopQrInterval();
    stopStatusCheckInterval();
    setQrcodeBase64('');
    setWahaPairingCode('');
    setError('');
    setView('list');
  }

  async function beginPairing() {
    if (operationRef.current) return;
    operationRef.current = true;
    pairingGenerationRef.current += 1;
    qrFailedRef.current = false;
    qrErrorCountRef.current = 0;
    pairingActiveRef.current = true;
    pairingModeRef.current = 'qr';
    setWahaConnectionMode('qr');
    setError('');
    setQrAttempts(0);
    setView('pairing');
    try { await handleFetchAllData(); } finally { operationRef.current = false; }
  }

  async function retryDeviceQuery() {
    setError('');
    setIsLoading(true);
    setDeviceLoad('loading');
    const result = await fetchWhatsAppConfig();
    if (result.provider) await handleFetchAllData(result.provider);
    else setIsLoading(false);
  }

  // ==========================================
  // CONNECT HANDLERS
  // ==========================================

  async function handleConnectWaha(e: React.FormEvent) {
    e.preventDefault();
    if (operationRef.current) return;
    operationRef.current = true;
    pairingGenerationRef.current += 1;
    qrFailedRef.current = false;
    qrErrorCountRef.current = 0;
    pairingActiveRef.current = true;
    pairingModeRef.current = 'qr';
    setWahaConnectionMode('qr');
    setView('pairing');
    clearStates();
    setIsLoading(true);

    try {
      const result = await connectWaha(wahaSessionName.trim());
      setWahaSessionName(result.sessionName || wahaSessionName);

      setMessage('WhatsApp configurado com sucesso!');
      setCurrentProvider('waha');
      currentProviderRef.current = 'waha';

      // Buscar todos os dados do WAHA
      await handleFetchAllData('waha');
    } catch (err: any) {
      console.error('❌ [handleConnectWaha] Erro ao conectar Whatsapp:', err);
      const errorDetail = err.response?.data?.detail;
      const errorMessage = Array.isArray(errorDetail)
        ? errorDetail.map(err => sanitizeWhatsAppUiText(err.msg)).join(', ')
        : typeof errorDetail === 'string'
          ? sanitizeWhatsAppUiText(errorDetail)
          : 'Erro ao conectar Whatsapp.';
      pairingActiveRef.current = false;
      setView('add');
      setError(errorMessage);
    } finally {
      operationRef.current = false;
      setIsLoading(false);
    }
  }

  // ==========================================
  // DISCONNECT/RESET HANDLERS
  // ==========================================

  async function handleDisconnect() {
    if (operationRef.current) return;
    operationRef.current = true;
    pairingGenerationRef.current += 1;
    qrFailedRef.current = false;
    qrErrorCountRef.current = 0;
    pairingActiveRef.current = true;
    pairingModeRef.current = 'qr';
    setWahaConnectionMode('qr');
    setView('pairing');
    setIsLoading(true);
    setError('');
    setMessage('');
    const generation = pairingGenerationRef.current;

    try {
      if (currentProvider === 'zapi') {
        await api.post('/webhook/whatsapp/disconnect');
        setMessage('WhatsApp desconectado com sucesso!');

        // Para Z-API, usar fluxo normal
        await new Promise(resolve => setTimeout(resolve, 500));
        await handleFetchAllData(undefined, true);


      } else if (currentProvider === 'waha') {
        // Salvar o nome da sessão ANTES de desconectar
        const sessionNameToReconnect = wahaSessionName;

        // 1. Desconectar a sessão atual
        await api.post('/webhook/whatsapp/disconnect');
        setMessage('Reconectando automaticamente...');

        // 2. Aguardar um pouco para o backend processar
        await new Promise(resolve => setTimeout(resolve, 1500));

        // 3. Reconectar automaticamente com o mesmo nome de sessão
        // Isso vai criar uma nova sessão com proxy Geonode
        try {
          if (!pairingActiveRef.current || generation !== pairingGenerationRef.current) return;
          await connectWaha(sessionNameToReconnect);

          // 4. Usar handleFetchAllData para sincronizar tudo corretamente
          // Isso vai descobrir o status real e atualizar a UI adequadamente
          await handleFetchAllData('waha', true);

          setMessage('Conexão pronta. Escaneie o QR Code para conectar.');

        } catch (reconnectErr: any) {
          console.error('❌ Erro ao reconectar:', reconnectErr);
          setError('Erro ao reconectar. Tente novamente.');
        }
      }
    } catch (err) {
      setError('Erro ao desconectar o WhatsApp.');
      console.error(err);
    } finally {
      operationRef.current = false;
      setIsLoading(false);
    }
  }

  async function handleResetConfig() {
    if (operationRef.current) return;
    operationRef.current = true;
    setShowResetModal(false);
    setIsLoading(true);
    try {
      await api.post('/webhook/whatsapp/reset');
      leaveFlow();
      setCurrentProvider(null);
      currentProviderRef.current = null;
      setWahaSessionName('');
      clearStates();
      setDeviceLoad('ready');
      setMessage('Configuração do dispositivo removida. A API oficial não foi alterada.');
    } catch {
      setError('Não foi possível remover a configuração do dispositivo.');
    } finally { operationRef.current = false; setIsLoading(false); }
  }

  async function handleManualRetry() {
    qrFailedRef.current = false;
    qrErrorCountRef.current = 0;
    setError('');
    setMessage('');
    setQrAttempts(0);
    setQrcodeBase64('');
    if (qrIntervalRef.current) {
      clearInterval(qrIntervalRef.current);
      qrIntervalRef.current = null;
    }
    await fetchQrCode();
    if (!statusDataRef.current?.connected) {
      startQrInterval();
    }
  }

  async function handleRequestWahaPairingCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    setWahaPairingCode('');

    if (!wahaPairingPhone.trim()) {
      setError('Informe o telefone com DDI e DDD. Exemplo: 5521999999999.');
      return;
    }

    if (!pairingActiveRef.current || operationRef.current) return;
    operationRef.current = true;
    const generation = pairingGenerationRef.current;
    setIsPairingLoading(true);

    try {
      const result = await requestWahaPairingCode(wahaPairingPhone.trim());
      if (!pairingActiveRef.current || generation !== pairingGenerationRef.current) return;

      if (result.connected || result.mode === 'connected') {
        setMessage('WhatsApp já está conectado.');
        stopQrInterval();
        stopStatusCheckInterval();
        await handleFetchAllData('waha', true);
        return;
      }

      const code = result.pairingCode || result.code || '';
      if (!code) {
        setError('Código de pareamento não disponível agora. Use o QR Code ou tente novamente.');
        return;
      }

      setWahaPairingCode(code);
      setMessage(result.message || 'Código gerado. Informe no WhatsApp para vincular o número.');

      if (!statusCheckIntervalRef.current) {
        startStatusCheckInterval();
      }
      if (!qrIntervalRef.current) {
        startQrInterval();
      }
    } catch (err: any) {
      console.error('Erro ao solicitar pairing code WAHA:', err);
      const errorDetail = err.response?.data?.detail;
      const errorMessage = Array.isArray(errorDetail)
        ? errorDetail.map(detail => sanitizeWhatsAppUiText(detail.msg)).join(', ')
        : typeof errorDetail === 'string'
          ? sanitizeWhatsAppUiText(errorDetail)
          : 'Não foi possível gerar o código. Use o QR Code ou tente novamente.';
      setError(errorMessage);
    } finally {
      operationRef.current = false;
      setIsPairingLoading(false);
    }
  }

  // ==========================================
  // INITIALIZATION
  // ==========================================

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('instagram_status') || url.searchParams.has('instagram')) {
      url.searchParams.delete('instagram');
      url.searchParams.delete('instagram_status');
      url.searchParams.delete('instagram_error');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    async function initialize() {
      setIsLoading(true);

      // Fetch Official WhatsApp Config
      try {
        await fetchOfficialConnection();
      } catch (err) {
        setOfficialConnectionLoadState('error');
        console.error('Failed to fetch official WhatsApp config:', err);
      }


      // Detectar provider configurado usando nova função unificada
      const { provider, hasConfig } = await fetchWhatsAppConfig();


      // Se detectou provider, buscar todos os dados
      if (hasConfig && provider) {
        await handleFetchAllData(provider);
      } else {
        // Sem configuração - mostrar tela de seleção
        setCurrentProvider(null);
        setIsLoading(false);
      }
    }

    initialize();

    return () => {
      pairingActiveRef.current = false;
      pairingGenerationRef.current += 1;
      stopQrInterval();
      stopStatusCheckInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const officialNeedsSignup = !officialConfig?.has_official
    && officialConfig?.status !== 'legacy_connected'
    && officialConfig?.status !== 'provisioning'
    && officialConfig?.status !== 'disconnecting'
    && officialConfig?.status !== 'disconnect_error';
  const officialAvailability = officialConnectionLoadState === 'error'
    ? 'error'
    : !officialNeedsSignup ? 'ready'
    : embeddedSignup.readiness === 'error'
      ? 'error'
      : officialConnectionLoadState === 'loading' || embeddedSignup.readiness === 'loading'
        ? 'loading'
        : 'ready';
  const officialAvailabilityMessage = officialConnectionLoadState === 'error'
    ? 'Não foi possível verificar a conexão oficial agora.'
    : embeddedSignup.preparationError;

  const theme = connectionTheme(isDark);
  const channelTheme = channelsTheme(isDark);
  const rows = getConnectionRows({ official: officialConfig, officialLoad: officialConnectionLoadState,
    provider: currentProvider, deviceLoad, device: deviceData, status: statusData });
  const officialAvailable = officialConnectionLoadState === 'ready' && !hasOfficialConnection(officialConfig);
  const deviceAvailable = deviceLoad === 'ready' && !currentProvider;
  const busy = isLoading || isPairingLoading || isOfficialLoading || embeddedSignup.isBusy || instagram.busy;
  const instagramAvailable = instagram.available && !instagram.loading && !instagram.error
    && (!instagram.connection || instagram.connection.status === 'disconnected');
  const canAdd = officialAvailable || deviceAvailable || instagramAvailable;
  const channelRows = whatsappChannelRows(rows);
  if (instagram.connection && instagram.connection.status !== 'disconnected') {
    channelRows.push(instagramChannelRow(instagram.connection, instagram.loading, Boolean(instagram.error)));
  } else if (instagram.pendingAccounts?.length) {
    channelRows.push({ kind: 'instagram', name: 'Instagram · Direct', identity: 'Perfil a selecionar',
      status: 'Selecione o perfil', tone: 'neutral', reconnect: false });
  }
  const manageChannel = (row: ChannelRow) => {
    if (row.kind === 'instagram') setView('instagram');
    else setView(row.reconnect ? 'recover' : row.kind);
  };
  const refreshChannels = async () => {
    setOfficialConnectionLoadState('loading');
    await Promise.allSettled([
      fetchOfficialConnection().catch(() => setOfficialConnectionLoadState('error')),
      retryDeviceQuery(), instagram.reload(),
    ]);
  };
  const confirmInstagramDisconnect = async () => {
    if (await instagram.disconnect()) {
      setShowInstagramDisconnect(false);
      setMessage('Instagram desconectado. O histórico foi mantido.');
      setView('list');
    }
  };

  const selectPairingMode = (mode: 'qr' | 'pairing') => {
    if (operationRef.current) return;
    qrFailedRef.current = false;
    qrErrorCountRef.current = 0;
    pairingModeRef.current = mode;
    pairingGenerationRef.current += 1;
    setWahaConnectionMode(mode);
    setWahaPairingCode('');
    setQrcodeBase64('');
    setError('');
    stopQrInterval();
    if (mode === 'qr') {
      void fetchQrCode();
      startQrInterval();
    }
  };

  return (
    <div className={`min-h-screen w-full ${channelTheme.canvas}`}>
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-8 px-5 pb-[max(32px,env(safe-area-inset-bottom))] pt-6 sm:px-7">
        <header className={`relative border-b pb-5 ${channelTheme.divider}`}>
          <p className="text-[10px] font-medium text-sminy-primary">CONFIGURAÇÕES</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">Canais</h1>
          <p className={`mt-2 text-xs ${channelTheme.body}`}><span className="sm:hidden">Conecte WhatsApp e Instagram.</span><span className="hidden sm:inline">Gerencie as conexões de WhatsApp e Instagram da sua clínica.</span></p>
          {view === 'list' && channelRows.length > 0 && canAdd && <AddChannelButton dark={isDark} disabled={busy} onClick={() => setView('channel-picker')} className="mt-6 w-full xl:absolute xl:right-0 xl:top-5 xl:mt-0 xl:w-auto" />}
        </header>
        {view !== 'list' && <div className="w-full max-w-[760px]"><ConnectionBack dark={isDark} disabled={busy} onClick={leaveFlow} /></div>}
        {message && <WhatsAppAlert type="success" text={message} isDark={isDark} />}
        {officialNotice && <WhatsAppAlert type="info" text={officialNotice} isDark={isDark} />}
        {error && <WhatsAppAlert type="error" text={error} isDark={isDark} />}

        {view === 'list' && <>
          {instagram.error && <WhatsAppAlert type="error" text={instagram.error} isDark={isDark} />}
          <ChannelsList rows={channelRows} dark={isDark} busy={busy} loading={instagram.loading || officialConnectionLoadState === 'loading' || deviceLoad === 'loading'} canAdd={canAdd}
            onAdd={() => setView('channel-picker')} onManage={manageChannel} onRefresh={() => void refreshChannels()} />
        </>}

        {view === 'channel-picker' && <ChannelPicker dark={isDark} busy={busy} whatsappAvailable={officialAvailable || deviceAvailable} instagramAvailable={instagramAvailable}
          onSelect={channel => { if (channel === 'whatsapp') setView('add'); else { setView('instagram'); void instagram.connect(); } }} />}

        {view === 'instagram' && <InstagramConnectionPanel state={instagram} dark={isDark} onDisconnect={() => setShowInstagramDisconnect(true)} />}

        {view === 'add' && <div className={connectionPanelClass(isDark)}><ConnectionPicker dark={isDark} officialAvailable={officialAvailable} deviceAvailable={deviceAvailable}
          onSelect={kind => setView(kind)} /></div>}

        {view === 'official' && <div className={connectionPanelClass(isDark)}>
          <WhatsAppOfficialAccountCard isDark={isDark} config={officialConfig}
            isLoading={isOfficialLoading || embeddedSignup.isBusy}
            availability={officialAvailability} availabilityMessage={officialAvailabilityMessage}
            signupPhase={embeddedSignup.phase} onConnect={handleConnectOfficial} onRefresh={handleRefreshOfficial}
            onRetryPreparation={handleRetryOfficialPreparation} onDisconnect={() => setShowOfficialDisconnectModal(true)} />
          {officialConnectionLoadState === 'error' && <div className="mt-4"><ConnectionButton dark={isDark} disabled={busy} onClick={handleRetryOfficialPreparation}>Consultar novamente</ConnectionButton></div>}
        </div>}

        {view === 'device' && <section className={connectionPanelClass(isDark)}>
          <h2 className="text-xl font-semibold">{currentProvider ? 'Dispositivo' : 'Conectar dispositivo'}</h2>
          <p className={`mt-3 text-[13px] ${theme.body}`}>{currentProvider ? 'Informações e ações desta conexão.' : 'Tenha o celular da clínica em mãos.'}</p>
          {deviceLoad === 'error' ? <div className={`mt-9 border-y py-6 ${theme.divider}`}>
            <p className="text-sm font-medium">Não foi possível verificar a conexão</p>
            <p className={`mt-3 text-xs leading-relaxed ${theme.body}`}>Sua configuração foi preservada. Uma falha de consulta não confirma uma desconexão.</p>
            <ConnectionLink dark={isDark} disabled={busy} onClick={retryDeviceQuery} className="mt-4">Consultar novamente</ConnectionLink>
          </div> : currentProvider ? <>
            <div className="mt-9">{rows.filter(row => row.kind === 'device').map(row => <DeviceConnectionProfile key={row.kind} row={row} dark={isDark} />)}</div>
            <div className="mt-2 flex justify-end"><ConnectionLink dark={isDark} disabled={busy} onClick={() => handleFetchAllData()}><RefreshCcw size={14} aria-hidden="true" />Verificar conexão</ConnectionLink></div>
            <div className={`mt-12 border-t pt-6 ${theme.divider}`}>
              <h3 className="text-sm font-medium">Reconectar dispositivo</h3>
              <p className={`mt-3 text-xs ${theme.muted}`}>Refaça o pareamento com o WhatsApp do celular.</p>
              <p className={`mt-3 text-xs ${theme.muted}`}>A API oficial permanece como está.</p>
              <ConnectionLink dark={isDark} disabled={busy} onClick={() => setView('recover')} className="mt-4">{statusData?.connected ? 'Refazer pareamento' : 'Reconectar dispositivo'} →</ConnectionLink>
            </div>
            <div className={`mt-8 border-t pt-6 ${theme.divider}`}>
              <h3 className="text-sm font-medium">Excluir esta conexão</h3>
              <p className={`mb-4 mt-3 text-xs leading-relaxed ${theme.muted}`}>Remove a configuração deste dispositivo. A API oficial permanece como está.</p>
              <ConnectionDeleteButton dark={isDark} disabled={busy} onClick={() => setShowResetModal(true)} />
            </div>
          </> : <div className="mt-9">
            <p className={`text-[13px] leading-relaxed ${theme.body}`}>Na próxima etapa, escolha QR Code ou código por telefone para autorizar o acesso pelo WhatsApp.</p>
            <form onSubmit={handleConnectWaha} className="mt-8"><ConnectionButton type="submit" dark={isDark} primary disabled={busy || !deviceAvailable} className="w-full !text-xs sm:w-auto">Iniciar conexão do dispositivo</ConnectionButton></form>
          </div>}
        </section>}

        {view === 'recover' && <section className={connectionPanelClass(isDark)}>
          <h2 className="text-xl font-semibold">Reconectar dispositivo</h2>
          <p className={`mt-3 text-[13px] ${theme.body}`}>Autorize novamente o WhatsApp da clínica pelo celular.</p>
          <div className="mt-9">{rows.filter(row => row.kind === 'device').map(row => <ConnectionIdentity key={row.kind} row={row} dark={isDark} />)}</div>
          <p className={`ml-16 mt-3 break-words text-xs ${theme.muted}`}>{deviceData?.phone || 'Número indisponível'}</p>
          <ol className={`mt-8 space-y-6 border-y py-6 ${theme.divider}`}>
            {[
              ['Preparar a sessão', statusData?.connected ? 'A sessão atual será encerrada e preparada para novo pareamento.' : 'A conexão ficará pronta para um novo pareamento.'],
              ['Autorizar no celular', 'Vincule o dispositivo pelo QR Code ou código.'],
              ['Conferir a conexão', 'A Sminy verifica o número e atualiza o status.'],
            ].map(([title, description], i) => <li key={title} className="flex gap-4"><span className={`pt-0.5 text-xs ${theme.muted}`}>{String(i + 1).padStart(2, '0')}</span><div><h3 className="text-sm font-medium">{title}</h3><p className={`mt-2 text-xs leading-relaxed ${theme.muted}`}>{description}</p></div></li>)}
          </ol>
          <p className={`mt-5 text-xs ${theme.body}`}>A conexão com a API oficial permanece como está.</p>
          <div className="mt-7 flex flex-col gap-4 sm:flex-row"><ConnectionButton dark={isDark} primary disabled={busy || !currentProvider} onClick={() => statusData?.connected ? handleDisconnect() : beginPairing()} className="!text-xs">Iniciar pareamento</ConnectionButton><ConnectionLink dark={isDark} disabled={busy} onClick={leaveFlow}>Cancelar</ConnectionLink></div>
          <div className={`mt-8 border-t pt-6 ${theme.divider}`}>
            <p className={`mb-4 text-xs ${theme.muted}`}>Se não for usar este dispositivo, você pode excluir a conexão.</p>
            <ConnectionDeleteButton dark={isDark} disabled={busy || !currentProvider} onClick={() => setShowResetModal(true)} />
          </div>
        </section>}

        {view === 'pairing' && currentProvider && <div className={connectionPanelClass(isDark)}>
          <WhatsAppPairingPanel isDark={isDark} provider={currentProvider} mode={wahaConnectionMode}
            qrcodeBase64={qrcodeBase64} qrAttempts={qrAttempts} pairingPhone={wahaPairingPhone} pairingCode={wahaPairingCode}
            isPairingLoading={isPairingLoading} onSelectQr={() => selectPairingMode('qr')} onSelectPairing={() => selectPairingMode('pairing')}
            onPairingPhoneChange={setWahaPairingPhone} onRequestPairingCode={handleRequestWahaPairingCode} onManualRetry={handleManualRetry} />
        </div>}

        {showResetModal && <WhatsAppConfirmModal isDark={isDark} variant="reset" connectionName={deviceData?.name || undefined} connectionPhone={deviceData?.phone || undefined} onCancel={() => setShowResetModal(false)} onConfirm={handleResetConfig} />}
        {showOfficialDisconnectModal && <WhatsAppConfirmModal isDark={isDark} variant="official-disconnect" connectionName={officialConfig?.verified_name || undefined} connectionPhone={officialConfig?.display_phone_number || undefined} onCancel={() => setShowOfficialDisconnectModal(false)} onConfirm={handleDisconnectOfficial} />}
        {showInstagramDisconnect && <InstagramDisconnectModal dark={isDark} username={instagram.connection?.username} busy={instagram.busy} error={instagram.error} onCancel={() => setShowInstagramDisconnect(false)} onConfirm={() => void confirmInstagramDisconnect()} />}
        {isLoading && view !== 'list' && <p role="status" className={`text-sm ${theme.muted}`}>Verificando conexão…</p>}
      </div>
    </div>
  );
};

export default WhatsAppConnectPage;
